import { useEffect } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import {
  AuthUserPermission,
  CONTACT_TYPE,
  RegisterVerifedUserCreateQuery,
  RegisterVerifedUserSendCodeQuery,
  RegisterVerifedUserSetLoginQuery,
} from '@cibo/core'
import { FriendlyError, Markdown } from '@cibo/ui'
import { useFormToken } from '../../queries'
import {
  createVerifiedUser,
  finalizeVerifiedUserRegistrationAndLogin,
  registerVerifyEmail,
  resetVerifiedUserFlow,
  selectRegisterVerifiedUserContactAddress,
  selectRegisterVerifiedUserContactType,
  selectRegisterVerifiedUserError,
  selectRegisterVerifiedUserJwt,
  selectRegisterVerifiedUserLoading,
  selectRegisterVerifiedUserUserId,
  selectRegisterVerifiedUserVerifyCode,
  selectRegisterVerifiedUserVerifyCodeSent,
  StoreWithRegisterVerifiedUser,
  StoreWithSession,
} from '../../store'

import { Stack } from '@mui/material'
import { Can } from '../Can'
import CreatePasswordForm from './CreatePasswordForm'
import { RegistrationStepper } from './RegistrationStepper'
import VerifyCodeForm from './VerifyCodeForm'
import VerifyEmailForm from './VerifyEmailForm'

type StoreProps = {
  registrationError: any
  registerLoading: any
  verifyCode: any
  verifyCodeSent: any
  contactAddress: any
  contactType: any
  userId: any
  userJwt: any

  createVerifiedUser(query: RegisterVerifedUserCreateQuery): void
  registerVerifyEmail(query: RegisterVerifedUserSendCodeQuery): void
  resetVerifiedUserFlow(): void
  finalizeVerifiedUserRegistrationAndLogin(query: RegisterVerifedUserSetLoginQuery): void
}

type PassProps = {
  onSuccess(): void
}

const mapStateToProps = (state: StoreWithSession & StoreWithRegisterVerifiedUser) => ({
  registrationError: selectRegisterVerifiedUserError(state),
  registerLoading: selectRegisterVerifiedUserLoading(state),
  verifyCode: selectRegisterVerifiedUserVerifyCode(state),
  verifyCodeSent: selectRegisterVerifiedUserVerifyCodeSent(state),
  contactAddress: selectRegisterVerifiedUserContactAddress(state),
  contactType: selectRegisterVerifiedUserContactType(state),
  userId: selectRegisterVerifiedUserUserId(state),
  userJwt: selectRegisterVerifiedUserJwt(state),
})

const mapDispatchToProps = {
  createVerifiedUser,
  registerVerifyEmail,
  resetVerifiedUserFlow,
  finalizeVerifiedUserRegistrationAndLogin,
}

type Formvalues = any

const RegisterVerifiedUserFormContainer = ({
  resetVerifiedUserFlow,
  registerVerifyEmail,
  createVerifiedUser,
  finalizeVerifiedUserRegistrationAndLogin,
  onSuccess,
  contactAddress,
  contactType,
  registerLoading,
  registrationError,
  t,
  userId,
  userJwt,
  verifyCode,
  verifyCodeSent,
}: StoreProps & PassProps & WithTranslation) => {
  const searchParams = new URLSearchParams(window.location.search)

  const {
    formAuthCode,
    formAuthToken,
    error,
    updateFormToken,
    isLoading: tokenLoading,
  } = useFormToken()

  const isLoading = registerLoading || tokenLoading

  useEffect(() => {
    if (!formAuthToken) {
      if (searchParams.has('code') && searchParams.has('formAuthToken')) {
        updateFormToken(searchParams.get('formAuthToken') as string)
      }
    }
  }, [formAuthToken])

  useEffect(() => {
    if (userJwt) {
      onSuccess && onSuccess()
    }
  }, [userJwt])

  const requestCode = (values: Formvalues) => {
    registerVerifyEmail({
      formAuthToken,
      ...values,
      contactType: CONTACT_TYPE.EMAIL,
    })
  }
  const createUser = (values: Formvalues) => createVerifiedUser(values)
  const finalizeUser = (values: Formvalues) => finalizeVerifiedUserRegistrationAndLogin(values)
  const handleStartOver = () => {
    resetVerifiedUserFlow()
    //kill all search params
    window.location.search = ''
  }

  if (error) {
    return <FriendlyError message={t('serverError')} />
  }

  if (!!formAuthToken) {
    if (userId) {
      return (
        <Stack spacing={2}>
          <RegistrationStepper activeStep={2} />
          <CreatePasswordForm
            error={error}
            isLoading={isLoading}
            formAuthToken={formAuthToken}
            userId={userId}
            verifyCode={verifyCode}
            onStartOver={handleStartOver}
            onSubmit={finalizeUser}
          />
        </Stack>
      )
    } else if (verifyCodeSent) {
      return (
        <Stack spacing={2}>
          <RegistrationStepper activeStep={1} />
          <VerifyCodeForm
            registrationError={registrationError}
            contactAddress={contactAddress}
            contactType={contactType}
            formAuthToken={formAuthToken}
            isLoading={isLoading}
            onStartOver={handleStartOver}
            onSubmit={createUser}
          />
        </Stack>
      )
    } else if (searchParams.has('code') && searchParams.has('contactAddress')) {
      return (
        <Stack spacing={2}>
          <RegistrationStepper activeStep={1} />
          <VerifyCodeForm
            code={searchParams.get('code') as string}
            registrationError={registrationError}
            contactAddress={searchParams.get('contactAddress') as string}
            contactType={contactType}
            formAuthToken={formAuthToken}
            isLoading={isLoading}
            onStartOver={handleStartOver}
            onSubmit={createUser}
          />
        </Stack>
      )
    }
  }

  return (
    <Stack spacing={2}>
      <RegistrationStepper activeStep={0} />
      <Can useAny={AuthUserPermission.USER_ROSTERED}>
        <Markdown color="text.secondary">{t('rosteredUserInstructionsMd')}</Markdown>
      </Can>
      <VerifyEmailForm
        error={registrationError}
        formAuthToken={formAuthToken}
        formAuthCode={formAuthCode}
        loading={isLoading || !formAuthToken}
        onSubmit={requestCode}
      />
    </Stack>
  )
}

export const RegisterVerifiedUserForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation('auth')(RegisterVerifiedUserFormContainer) as any)
